import { useEffect, useState } from "react";
import {
  Globe2,
  LockKeyhole,
  ShieldCheck,
  Target,
  UsersRound,
} from "lucide-react";
import Button from "../components/Button";
import CTA from "../components/public/CTA";
import FAQSection from "../components/public/FAQSection";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import ServiceCard from "../components/ServiceCard";
import TestimonialStrip from "../components/public/TestimonialStrip";
import { proofPoints } from "../data/siteContent";
import { services } from "../data/services";

const heroServiceLabels = [
  "Automobile services",
  "Logistics",
  "Clearing services",
  "Property services",
  "Concierge services",
  "Procurement",
  "Compliance",
];

const proofIcons = [UsersRound, Target, Globe2];

export function Home() {
  const [heroServiceIndex, setHeroServiceIndex] = useState(0);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroServiceIndex(
        (current) => (current + 1) % heroServiceLabels.length,
      );
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, []);
  const highlightedService = heroServiceLabels[heroServiceIndex];
  return (
    <>
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 bg-[url('/brand/bridge-banner.jpeg')] bg-cover bg-center opacity-25" />
        <div className="tech-grid absolute inset-0 opacity-50" />
        <div className="absolute -left-40 top-24 h-[520px] w-[520px] rounded-full bg-ocean/20 blur-[130px]" />
        <div className="absolute -right-48 bottom-[-100px] h-[520px] w-[520px] rounded-full bg-bridge/15 blur-[130px]" />
        <div className="container relative grid min-h-[720px] items-center gap-14 pb-20 pt-36 lg:grid-cols-[1.05fr_.95fr] lg:gap-20 lg:pb-24 lg:pt-44">
          <Reveal className="relative w-full text-center lg:col-start-1 lg:text-left">
            <p className="eyebrow text-cyan">
              <span className="mr-3 inline-block h-px w-10 align-middle bg-cyan" />
              Your trusted execution partner in Nigeria
            </p>
            <h1 className="mt-7 max-w-4xl font-display text-[2rem] font-extrabold leading-[.98] tracking-[-.065em] sm:text-[2.94rem] lg:text-[3.5rem] xl:text-[4.5rem]">
              Connecting global clients to{" "}
              <span
                key={highlightedService}
                aria-live="polite"
                className="block animate-slide-up text-cyan"
              >
                {highlightedService}.
              </span>
            </h1>
            {/* <p className="mt-7 max-w-xl text-base leading-8 text-slate-300 sm:text-lg">
              The Bridge connects global clients to dependable local
              execution—from vehicles and logistics to property, procurement,
              concierge and compliance.
            </p> */}
            <div className="mt-9 flex flex-nowrap justify-center gap-2 lg:justify-start sm:gap-3">
              <Button to="/signup" className="whitespace-nowrap">
                Get started
              </Button>
              <Button
                to="/how-it-works"
                variant="light"
                className="whitespace-nowrap"
              >
                See how it works
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-slate-300 lg:justify-start lg:gap-8">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={17} className="text-bridge" />
                Accountable operations
              </span>
              <span className="inline-flex items-center gap-2">
                <LockKeyhole size={17} className="text-bridge" />
                Secure milestones
              </span>
            </div>
          </Reveal>
          <Reveal delay={130} className="relative lg:col-start-2 lg:col-end-3">
            <div className="relative mx-auto max-w-[520px] overflow-hidden rounded-[2rem] border border-white/15 bg-white/[.06] px-3 shadow-lift backdrop-blur">
              <img
                src="/brand/bridge-banner.jpeg"
                alt="The Bridge by VA Connecting Opportunities"
                className="h-[410px] w-full rounded-[1.5rem] object-cover object-center opacity-90 sm:h-[490px]"
              />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="border-b border-ink/10 bg-white">
        <div className="container grid gap-8 py-8 sm:grid-cols-3">
          {proofPoints.map(([, title, copy], index) => {
            const Icon = proofIcons[index];
            return (
              <div key={title} className="flex gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-ocean/10 text-ocean">
                  <Icon size={28} strokeWidth={2.2} aria-hidden="true" />
                </span>
                <div>
                  <p className="font-display font-bold text-ink">{title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate">{copy}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="section-space bg-mist" id="services">
        <div className="container">
          <SectionHeading
            eyebrow="What we move"
            title="Practical services. Thoughtful execution."
            copy="Tell us what needs to happen in Nigeria. We will bring the structure, local context and operational ownership to move it forward."
          />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <ServiceCard key={service.slug} service={service} index={index} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button to="/services" variant="outline">
              Explore all service lines
            </Button>
          </div>
        </div>
      </section>
      <section className="section-space bg-white">
        <div className="container grid items-center gap-12 lg:grid-cols-[.9fr_1.1fr]">
          <Reveal>
            <SectionHeading
              eyebrow="A calmer way to get things done"
              title="Distance should not mean uncertainty."
              copy="We are building the operating layer Nigerians abroad, local customers and growing organisations need to act with confidence. Every request gets a clear owner, a considered plan and visible next steps."
            />
            <Button to="/about" className="mt-8">
              Meet The Bridge
            </Button>
          </Reveal>
          <Reveal delay={100} className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl bg-ink p-7 text-white sm:translate-y-8">
              <Globe2 className="text-cyan" />
              <p className="mt-12 font-display text-5xl font-extrabold">01</p>
              <p className="mt-3 font-bold">One accountable team</p>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Internal operations coordinate the work in this phase.
              </p>
            </div>
            <div className="rounded-3xl bg-mist p-7">
              <Target className="text-ocean" />
              <p className="mt-12 font-display text-5xl font-extrabold text-ink">
                360°
              </p>
              <p className="mt-3 font-bold text-ink">Visibility by design</p>
              <p className="mt-2 text-sm leading-6 text-slate">
                Milestones, documents, messages and updates in one place.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <TestimonialStrip />
      <FAQSection />
      <CTA />
    </>
  );
}
